import { Link } from "wouter";
import { Mail, Zap, Shield, CheckCircle, FileCode2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Landing() {
  return (
    <div className="min-h-screen bg-background text-foreground selection:bg-primary/20">
      <header className="border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 font-bold text-xl tracking-tight">
            <Mail className="h-6 w-6 text-primary" />
            <span>MailCraft</span>
          </div>
          <div className="flex items-center gap-4">
            <Link href="/sign-in" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
              Sign in
            </Link>
            <Link href="/sign-up">
              <Button size="sm" className="font-semibold shadow-sm">Get Started</Button>
            </Link>
          </div>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="py-24 md:py-32 px-4 text-center max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-8">
            <Zap className="h-4 w-4" />
            <span>No SMTP setup. No DNS. Just send.</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-foreground mb-6 leading-tight">
            The quiet, capable engine for your <span className="text-primary">transactional email</span>.
          </h1>
          <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto leading-relaxed">
            A premium utility for founders and ops teams. Sign up, draft a template with AI, hit send. Delivery is on us &mdash; no SMTP credentials, no DNS records, no warm-up.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link href="/sign-up">
              <Button size="lg" className="text-base h-12 px-8 font-semibold shadow-sm">
                Start sending for free
              </Button>
            </Link>
          </div>
        </section>

        {/* Features */}
        <section className="py-20 bg-secondary/50 border-t border-border/40">
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-card border border-card-border rounded-xl p-6 shadow-sm">
                <Shield className="h-8 w-8 text-primary mb-4" />
                <h3 className="text-lg font-semibold mb-2">Delivery handled for you</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Every email leaves through MailCraft's verified shared sender, with SPF, DKIM, and DMARC already in place. You pick the From name &mdash; we handle the rest.
                </p>
              </div>
              <div className="bg-card border border-card-border rounded-xl p-6 shadow-sm">
                <FileCode2 className="h-8 w-8 text-primary mb-4" />
                <h3 className="text-lg font-semibold mb-2">AI-assisted templates</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Describe what you need &mdash; an invoice, a welcome email, a reset password link &mdash; and our AI drafts a clean, responsive HTML template.
                </p>
              </div>
              <div className="bg-card border border-card-border rounded-xl p-6 shadow-sm">
                <CheckCircle className="h-8 w-8 text-primary mb-4" />
                <h3 className="text-lg font-semibold mb-2">Pristine logging</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Every send is logged with its fully rendered HTML body and exact variables. Never guess what a user actually received.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-border/40 py-12 bg-background">
        <div className="container mx-auto px-4 text-center text-muted-foreground text-sm">
          &copy; {new Date().getFullYear()} MailCraft. Built with precision.
        </div>
      </footer>
    </div>
  );
}
